import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Backdrop } from './backdrop';
import { Brand } from './brand';
import { Mascot } from './mascot';
import type { MascotPose } from './mascot';
import { cx } from './styles';

const tones = {
  neutral: 'bg-slate-700 text-white',
  warning: 'bg-amber-400 text-amber-950',
  danger: 'bg-rose-500 text-white',
  brand: 'bg-brand-600 text-white',
};

interface StatusScreenProps {
  readonly icon: ReactNode;
  readonly tone?: keyof typeof tones;
  readonly pose?: MascotPose;
  readonly eyebrow?: string;
  readonly title: string;
  readonly children?: ReactNode;
  readonly actions?: ReactNode;
}

export function StatusScreen({
  icon,
  tone = 'brand',
  pose = 'think',
  eyebrow,
  title,
  children,
  actions,
}: StatusScreenProps) {
  return (
    <Backdrop className="flex min-h-screen flex-col items-center justify-center px-4 py-12">
      <Link to="/" aria-label="Naar de startpagina" className="mb-8 rounded-xl">
        <Brand size="lg" />
      </Link>
      <main className="w-full max-w-md rounded-4xl bg-white px-6 pt-10 pb-8 text-center shadow-warm ring-1 ring-slate-200 motion-safe:animate-rise sm:px-10">
        <div className="relative mx-auto w-fit">
          <span
            aria-hidden="true"
            className="absolute inset-x-2 -bottom-1 h-4 rounded-full bg-leaf-800/10 blur-sm"
          />
          <Mascot pose={pose} size="lg" className="relative drop-shadow-md" />
          <span
            className={cx(
              'absolute -right-1 bottom-0 flex size-11 items-center justify-center rounded-2xl shadow-md ring-4 ring-white [&_svg]:size-5',
              tones[tone],
            )}
          >
            {icon}
          </span>
        </div>
        {eyebrow ? (
          <p className="mt-7 text-xs font-semibold tracking-widest text-brand-700 uppercase">
            {eyebrow}
          </p>
        ) : null}
        <h1
          className={cx(
            'font-rounded text-[1.75rem] leading-tight font-semibold tracking-tight text-brand-950',
            eyebrow ? 'mt-2' : 'mt-7',
          )}
        >
          {title}
        </h1>
        {children ? (
          <div className="mx-auto mt-3 max-w-sm text-[0.9375rem] leading-relaxed text-slate-600">
            {children}
          </div>
        ) : null}
        {actions ? (
          <div className="mt-8 flex flex-col-reverse justify-center gap-2 sm:flex-row">
            {actions}
          </div>
        ) : null}
      </main>
    </Backdrop>
  );
}
